/* eslint-disable max-statements */
import { ApolloError } from '@apollo/client';
import { getProviderTokens } from '@auth/api';
import { CREATE_SESSION, GET_SESSION } from '@auth/graphql';
import { useAuthProviders } from '@auth/hooks';
import { setUser } from '@auth/state';
import { clearPkceStorage, clearProviderId, extractRedirectFromState, validateCallbackParams } from '@auth/utils';
import { routes as communityRoutes } from '@communities/routes';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { ErrorCategory } from '@core/domain';
import { routes } from '@core/routes';
import { apolloClient } from '@core/services/ApolloClient';
import { loadPreferences } from '@core/services/userPreferencesStorage';
import { track } from '@soteria-ui/src/core/logging';
import { useAppDispatch } from '@store';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export function useAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { providers, loading: providersLoading } = useAuthProviders();

  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  useEffect(() => {
    let processed = false;

    // eslint-disable-next-line complexity
    async function handleCallback() {
      if (processed) {
        return;
      }

      // Wait for providers to finish loading before proceeding
      if (providersLoading) {
        return;
      }

      processed = true;

      try {
        track('LoginCallbackInitiated');
        setProcessing(true);
        setErrors(null);

        const params = new URLSearchParams(location.search);
        const { code, providerId, codeVerifier, state } = validateCallbackParams(params);

        const provider = providers.find(p => p.id === providerId);

        if (!provider) {
          throw new Error(`Auth provider not found: ${providerId}`);
        }

        // 1. Get OAuth tokens from the identity provider
        const redirectUri = `${window.location.origin}${routes.loginCallback}`;
        const tokens = await getProviderTokens({
          code,
          codeVerifier,
          redirectUri,
          provider,
        });

        // 2. Create a session with the BFF
        const createResult = await apolloClient.mutate({
          mutation: CREATE_SESSION,
          variables: { tokens, providerId },
        });

        if (!createResult.data?.createSession?.success) {
          const errorMessage = createResult.data?.createSession?.message || 'Unknown error';

          throw new Error(`Failed to create session: ${errorMessage}`);
        }

        // 3. Clear all PKCE-related storage after successful authentication
        clearPkceStorage();
        clearProviderId();

        // 4. Fetch session data and update Redux state
        const { data, error: sessionError } = await apolloClient.query({
          query: GET_SESSION,
          fetchPolicy: 'network-only',
        });

        if (sessionError) {
          throw sessionError;
        }

        const session = data?.session || null;

        if (!session) {
          throw new Error('No session data received');
        }

        dispatch(setUser(session.user));

        track('LoginCallbackSucceeded', {
          providerId: provider.id,
        });

        // 5. Extract redirect URL from state parameter
        // Priority: state redirect > user's home page preference > default
        const stateRedirectUrl = extractRedirectFromState(state);

        let finalRedirectUrl: string;

        if (stateRedirectUrl) {
          // Use explicit redirect from login state (e.g., deep link)
          finalRedirectUrl = stateRedirectUrl;
        } else {
          // Check user's home page preference
          const userPreferences = loadPreferences();

          finalRedirectUrl = userPreferences.homePage || communityRoutes.communityGroups.root;
        }

        navigate(finalRedirectUrl);
      } catch (generalError) {
        // Check for Apollo errors first (GraphQL errors)
        if (generalError instanceof ApolloError) {
          const transformedErrors = transformErrors(generalError);

          setErrors(transformedErrors);

          track('LoginCallbackFailed', {
            errorType: 'apollo_error',
            errorMessage: transformedErrors[0]?.userMessage?.substring(0, 100) || 'unknown',
          });
        } else {
          // Convert all other errors to DomainError format for consistency
          const errorMessage = generalError instanceof Error ? generalError.message : String(generalError);
          const domainError: DomainError = {
            code: 'AUTH_CALLBACK_ERROR',
            message: errorMessage,
            userMessage: 'Authentication failed. Please try again.',
            category: ErrorCategory.AUTHENTICATION,
          };

          setErrors([domainError]);

          track('LoginCallbackFailed', {
            errorType: generalError instanceof Error ? generalError.name : 'unknown',
            errorMessage: errorMessage.substring(0, 100),
          });
        }

        clearPkceStorage();
        clearProviderId();
      } finally {
        setProcessing(false);
      }
    }

    handleCallback();
  }, [location.search, providers, providersLoading, dispatch, navigate]); // Include all necessary dependencies

  return {
    processing: processing || providersLoading,
    errors,
  };
}
